import _ from 'lodash';
import { ReactNode, useMemo } from 'react';
import moment from 'moment';
import { t } from 'i18next';
import {
  DAYS,
  formatTimeDelta,
  mergeOverlappingPeriods,
  splitScheduleRangeByDayBoundary,
  TRecruitmentSchedule
} from '../../../../common/recruitment';
import { ComplexTooltip } from '../../../components/presentational/tooltip';
import { formatTimeRange } from '../../../libs/utils';

export interface ISchedulePreviewProps {
  timezone?: string;
  schedule: TRecruitmentSchedule[];
}

export function SchedulePreview(props: ISchedulePreviewProps) {
  const normalizedSchedule = useMemo(() => {
    const splitSchedule = props.schedule.map(splitScheduleRangeByDayBoundary);
    return mergeOverlappingPeriods(splitSchedule.flat());
  }, [props.schedule]);

  const times = useMemo(() => {
    return {
      starts: _.uniq(normalizedSchedule.map((entry) => entry.start_minutes)),
      ends: _.uniq(normalizedSchedule.map((entry) => entry.end_minutes))
    };
  }, [normalizedSchedule]);

  const items: ReactNode[] = [];

  for (const dayInfo of DAYS) {
    const schedulesEntries = normalizedSchedule.filter((entry) => entry.day_of_week === dayInfo.id);

    if (schedulesEntries.length > 0) {
      items.push(
        <div key={dayInfo.id} className="slds-col slds-text-align--center">
          <ComplexTooltip
            className="d-inline-block"
            placement="top"
            tooltip={
              <>
                <div>{t(`recruitment:day-of-week.${dayInfo.slug}.name`)}</div>
                {schedulesEntries.map((entry, index) => (
                  <div key={index}>
                    {entry.start_minutes === entry.end_minutes ? (
                      <div>{t('recruitment:schedule.any-time')}</div>
                    ) : (
                      <>
                        <div>{formatTimeRange(entry.start_minutes, entry.end_minutes)}</div>
                        <div>{formatTimeDelta(entry.start_minutes, entry.end_minutes)}</div>
                      </>
                    )}
                  </div>
                ))}
              </>
            }
          >
            <span className="rio-rec-tag">{t(`recruitment:day-of-week.${dayInfo.slug}.abbreviation`)}</span>
          </ComplexTooltip>
        </div>
      );
    } else {
      items.push(
        <div key={dayInfo.id} className="slds-col slds-text-align--center">
          <span className="rio-rec-tag" style={{ opacity: 0.4 }}>
            {t(`recruitment:day-of-week.${dayInfo.slug}.abbreviation`)}
          </span>
        </div>
      );
    }
  }

  const tz = moment.tz(props.timezone || 'UTC').format('z');

  return (
    <div
      className="slds-grid slds-grid--vertical slds-text-align--center slds-grid--vertical-align-center"
      style={{ whiteSpace: 'normal' }}
    >
      <div className="slds-grid slds-grid--vertical-align-center">
        <div className="slds-col">
          {times.starts.length === 1 && times.ends.length === 1 ? (
            formatTimeRange(times.starts[0], times.ends[0])
          ) : (
            <i className="text-muted">{t('recruitment:schedule.multiple-times')}</i>
          )}
        </div>
        {props.timezone && (
          <div className="slds-col recruitment-card--tz slds-m-left--x-small">
            <ComplexTooltip placement="top" tooltip={`${t('recruitment:tooltip.timezone')} ${props.timezone || 'UTC'}`}>
              <>
                <i className="fa fa-globe slds-m-right--xx-small" />
                {tz}
              </>
            </ComplexTooltip>
          </div>
        )}
      </div>
      <div className="slds-grid slds-grid-align--center">{items}</div>
    </div>
  );
}
